const labelSelectorSchema = {
  type: 'object',
  properties: {
    matchLabels: { type: 'object', additionalProperties: { type: 'string' } },
    matchExpressions: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          key: { type: 'string' },
          operator: { enum: ['In', 'NotIn', 'Exists', 'DoesNotExist'] },
          values: {
            type: 'array',
            items: { type: 'string' },
          },
        },
        required: ['key', 'operator'],
        additionalProperties: false,
      },
    },
  },
  additionalProperties: false,
};

const requeueAfterSecondsSchema = { type: 'integer', minimum: 60 };

const listGeneratorSchema = {
  type: 'object',
  properties: {
    elements: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
    elementsYaml: { type: 'string' },
  },
  anyOf: [{ required: ['elements'] }, { required: ['elementsYaml'] }],
  additionalProperties: false,
};

const clustersGeneratorSchema = {
  type: 'object',
  properties: {
    selector: labelSelectorSchema,
    flatList: { type: 'boolean' },
  },
  additionalProperties: false,
};

const gitPathItemSchema = {
  type: 'object',
  properties: {
    path: { type: 'string' },
    exclude: { type: 'boolean' },
  },
  required: ['path'],
  additionalProperties: false,
};

const gitGeneratorSchema = {
  type: 'object',
  properties: {
    repoURL: { type: 'string' },
    revision: { type: 'string' },
    directories: {
      type: 'array',
      items: gitPathItemSchema,
    },
    files: {
      type: 'array',
      items: gitPathItemSchema,
    },
    requeueAfterSeconds: requeueAfterSecondsSchema,
    pathParamPrefix: { type: 'string' },
  },
  required: ['repoURL', 'revision'],
  anyOf: [{ required: ['directories'] }, { required: ['files'] }],
  additionalProperties: false,
};

const pullRequestGeneratorSchema = {
  type: 'object',
  properties: {
    github: {
      type: 'object',
      properties: {
        owner: { type: 'string' },
        repo: { type: 'string' },
        labels: {
          type: 'array',
          items: { type: 'string' },
        },
      },
      required: ['owner', 'repo'],
      additionalProperties: false,
    },
    filters: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          branchMatch: { type: 'string' },
          targetBranchMatch: { type: 'string' },
          titleMatch: { type: 'string' },
        },
        additionalProperties: false,
      },
    },
    requeueAfterSeconds: requeueAfterSecondsSchema,
    continueOnRepoNotFoundError: { type: 'boolean' },
  },
  required: ['github'],
  additionalProperties: false,
};

// Wraps a generator as `{ <key>: <schema>, selector? }`, the shape of one
// entry in a `generators` array.
const generatorEntry = (key: string, schema: object) => ({
  properties: {
    [key]: schema,
    selector: labelSelectorSchema,
  },
  required: [key],
  additionalProperties: false,
});

const terminalGenerators = [
  generatorEntry('list', listGeneratorSchema),
  generatorEntry('clusters', clustersGeneratorSchema),
  generatorEntry('git', gitGeneratorSchema),
  generatorEntry('pullRequest', pullRequestGeneratorSchema),
];

const terminalGeneratorItemSchema = {
  type: 'object',
  oneOf: terminalGenerators,
};

const matrixGeneratorSchema = (items: object) => ({
  type: 'object',
  properties: {
    generators: {
      type: 'array',
      minItems: 2,
      maxItems: 2,
      items,
    },
  },
  required: ['generators'],
  additionalProperties: false,
});

const mergeGeneratorSchema = (items: object) => ({
  type: 'object',
  properties: {
    generators: {
      type: 'array',
      minItems: 2,
      items,
    },
    mergeKeys: {
      type: 'array',
      minItems: 1,
      items: { type: 'string' },
    },
  },
  required: ['generators', 'mergeKeys'],
  additionalProperties: false,
});

// Matrix/merge nested inside another matrix/merge: terminal children only.
const nestedGeneratorItemSchema = {
  type: 'object',
  oneOf: [
    ...terminalGenerators,
    generatorEntry(
      'matrix',
      matrixGeneratorSchema(terminalGeneratorItemSchema),
    ),
    generatorEntry('merge', mergeGeneratorSchema(terminalGeneratorItemSchema)),
  ],
};

const generatorItemSchema = {
  type: 'object',
  oneOf: [
    ...terminalGenerators,
    generatorEntry('matrix', matrixGeneratorSchema(nestedGeneratorItemSchema)),
    generatorEntry('merge', mergeGeneratorSchema(nestedGeneratorItemSchema)),
  ],
};

export const argoCDGeneratorSchema = {
  type: 'object',
  properties: {
    generators: {
      type: 'array',
      items: generatorItemSchema,
    },
  },
  required: ['generators'],
};
